<template>
  <div class="console-card" style="overflow:hidden;">
    <button type="button" class="assets-head" :aria-expanded="open" @click="open = !open">
      <span style="font-weight:600;">Source files</span>
      <span v-if="assets.length" style="color:var(--muted);">{{ assets.length }}</span>
      <span style="flex:1;" />
      <UIcon v-if="open" name="i-heroicons-chevron-up" class="h-4 w-4" />
      <UIcon v-else name="i-heroicons-chevron-down" class="h-4 w-4" />
    </button>

    <div v-show="open" style="padding:14px; border-top:1px solid var(--line);">
      <p v-if="!assets.length" style="margin:0; color:var(--muted);">No design or copy files are attached to this email.</p>

      <div v-else class="flex flex-col gap-4">
        <div v-for="grp in grouped" :key="grp.role" class="flex flex-col gap-2">
          <div class="console-label">{{ grp.label }}</div>

          <a
            v-for="(asset, i) in grp.items"
            :key="i"
            :href="asset.url"
            target="_blank"
            rel="noopener"
            class="asset"
            :title="`Open ${asset.name}`"
          >
            <img v-if="isImage(asset)" :src="asset.url" :alt="asset.name" loading="lazy">
            <span class="asset-name">
              <UIcon name="i-heroicons-arrow-top-right-on-square" class="h-4 w-4 shrink-0" />
              <span class="truncate" style="flex:1;">{{ asset.name }}</span>
              <span v-if="!isImage(asset)" style="color:var(--muted); font-size:13px;">{{ extLabel(asset) }}</span>
            </span>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{ assets?: EmailAssetT[] }>()

const open = ref(true)

const assets = computed<EmailAssetT[]>(() => props.assets ?? [])

const roleMeta: Record<string, { label: string, order: number }> = {
  design_desktop: { label: 'Desktop design', order: 0 },
  design_mobile: { label: 'Mobile design', order: 1 },
  copy: { label: 'Copy', order: 2 },
  other: { label: 'Other files', order: 3 },
}

const grouped = computed(() => {
  const buckets = new Map<string, EmailAssetT[]>()
  for (const asset of assets.value) {
    const role = roleMeta[asset.role] ? asset.role : 'other'
    if (!buckets.has(role)) buckets.set(role, [])
    buckets.get(role)!.push(asset)
  }
  return [...buckets.entries()]
    .map(([role, items]) => ({ role, items, ...roleMeta[role]! }))
    .sort((a, b) => a.order - b.order)
})

function isImage(asset: EmailAssetT): boolean {
  if (asset.type && asset.type.startsWith('image/')) return true
  if (asset.role === 'design_desktop' || asset.role === 'design_mobile') return true
  return /\.(png|jpe?g|gif|webp|avif|svg)(\?|#|$)/i.test(asset.url || '')
}

function extLabel(asset: EmailAssetT): string {
  const fromName = asset.name?.split('.').pop()
  if (fromName && fromName !== asset.name) return fromName.toLowerCase()
  const fromType = asset.type?.split('/').pop()
  return (fromType || 'file').toLowerCase()
}
</script>

<style scoped>
.assets-head {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 14px;
  border: none;
  background: transparent;
  color: var(--ink);
  cursor: pointer;
  text-align: left;
}
.asset {
  display: block;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--panel2);
  overflow: hidden;
}
.asset:hover {
  border-color: var(--line2);
}
.asset img {
  display: block;
  width: 100%;
  max-height: 220px;
  object-fit: contain;
  background: var(--well);
}
.asset-name {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 11px;
  min-width: 0;
}
</style>
